import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { GripVertical, Pencil, X, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { usePageMeta } from "../../components/Layout";

// C-B Step 5 — Admin Content Library.
// Uses the accepted /api/admin/content-library/* endpoints (no new
// backend surface).  Historical immutability visibly enforced in UI
// (published versions are read-only; only the composer publishes a
// NEW version).

const CLASSES = ["operational", "formal_issue", "commercial", "contractual"];
const MODES = ["automatic", "admin_confirmed", "manual", "contract_controlled"];
const STATUSES = ["fixture", "draft", "in_review", "approved", "production"];

function StatusPill({ value }) {
    const tone = {
        production: "bg-emerald-100 text-emerald-800",
        approved: "bg-blue-100 text-blue-800",
        in_review: "bg-amber-100 text-amber-800",
        draft: "bg-slate-100 text-slate-700",
        fixture: "bg-rose-100 text-rose-700",
    }[value] || "bg-slate-100 text-slate-700";
    return (
        <span data-testid="cl-status-pill"
                className={`text-[11px] font-manrope font-semibold px-2 py-0.5 rounded ${tone}`}>
            {value || "fixture"}
        </span>
    );
}

export function ContentLibraryList() {
    usePageMeta({ title: "Content Library | Studio G4 Admin" });
    const [items, setItems] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [err, setErr] = useState("");
    // §CL List Pass — multi-select chip filters (client-side).
    const [wsSel, setWsSel] = useState(new Set());
    const [classSel, setClassSel] = useState(new Set());
    const [modeSel, setModeSel] = useState(new Set());
    const [statusSel, setStatusSel] = useState(new Set());

    const load = useCallback(() => {
        setErr("");
        api.get("/admin/content-library/templates?limit=200")
            .then(r => { setItems(r.data.items || []); setMarkers(r.data.markers || []); })
            .catch(e => setErr(e?.response?.data?.detail?.message || e.message));
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = (setter) => (key) => setter(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const filtered = useMemo(() => (items || []).filter(({ current }) => {
        if (!current) return true;
        if (classSel.size && !classSel.has(current.communication_class)) return false;
        if (modeSel.size && !modeSel.has(current.delivery_control_mode)) return false;
        if (statusSel.size && !statusSel.has(current.content_status)) return false;
        if (wsSel.size) {
            const rowWs = new Set((current.applicable_workstages || []).map(String));
            let matched = false;
            for (const w of wsSel) { if (rowWs.has(w)) { matched = true; break; } }
            if (!matched) return false;
        }
        return true;
    }), [items, wsSel, classSel, modeSel, statusSel]);

    // §Item 3 — drag-and-drop reorder.  Local optimistic list; PATCH
    //   is debounced so rapid drags collapse into a single API call.
    const [dragIndex, setDragIndex] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const pendingOrderRef = useRef(null);
    const debounceRef = useRef(null);
    const flushOrder = useCallback((entries) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        pendingOrderRef.current = entries;
        debounceRef.current = setTimeout(() => {
            api.patch("/admin/content-library/templates/order",
                { order: pendingOrderRef.current })
                .catch(e => toast.error(e?.response?.data?.detail?.message || "Reorder failed"));
        }, 300);
    }, []);
    // §Section Markers UI — merged (templates + markers) sequence.  Markers
    //   are NEVER filtered by chip groups; only templates are.  DnD works
    //   across both row types using the polymorphic reorder body shape.
    const merged = useMemo(() => {
        const combined = [
            ...(items || []).map(t => ({ __row: "template", key: t.head.template_code, ...t })),
            ...(markers || []).map(m => ({ __row: "marker", key: m.id, ...m })),
        ];
        combined.sort((a, b) =>
            ((a.__row === "marker" ? a.display_order : a.head?.display_order) ?? 1e9)
            - ((b.__row === "marker" ? b.display_order : b.head?.display_order) ?? 1e9)
            || (a.__row === "marker" ? a.id : (a.head?.template_code || "")).localeCompare(
                 b.__row === "marker" ? b.id : (b.head?.template_code || "")));
        return combined;
    }, [items, markers]);
    const mergedFiltered = useMemo(() => merged.filter(row => {
        if (row.__row === "marker") return true;   // always render markers
        const c = row.current;
        if (!c) return true;
        if (classSel.size && !classSel.has(c.communication_class)) return false;
        if (modeSel.size && !modeSel.has(c.delivery_control_mode)) return false;
        if (statusSel.size && !statusSel.has(c.content_status)) return false;
        if (wsSel.size) {
            const rowWs = new Set((c.applicable_workstages || []).map(String));
            let matched = false;
            for (const w of wsSel) { if (rowWs.has(w)) { matched = true; break; } }
            if (!matched) return false;
        }
        return true;
    }), [merged, wsSel, classSel, modeSel, statusSel]);
    const asOrderEntry = (row) => row.__row === "marker"
        ? { type: "marker", id: row.id }
        : { type: "template", code: row.head.template_code };

    const handleDrop = (targetKey) => {
        if (dragIndex === null) { setDragIndex(null); setDropTarget(null); return; }
        const src = merged.findIndex(r => r.key === dragIndex);
        const dst = merged.findIndex(r => r.key === targetKey);
        if (src === -1 || dst === -1 || src === dst) {
            setDragIndex(null); setDropTarget(null); return;
        }
        const next = merged.slice();
        const [moved] = next.splice(src, 1);
        next.splice(dst, 0, moved);
        // Split back into templates + markers with new display_order.
        const nextItems = [], nextMarkers = [];
        next.forEach((row, idx) => {
            const display_order = idx + 1;
            if (row.__row === "template") {
                nextItems.push({ ...row, head: { ...row.head, display_order } });
            } else {
                nextMarkers.push({ ...row, display_order });
            }
        });
        // Strip synthetic keys before persisting to state.
        setItems(nextItems.map(({ __row, key, ...t }) => t));
        setMarkers(nextMarkers.map(({ __row, key, ...m }) => m));
        setDragIndex(null); setDropTarget(null);
        flushOrder(next.map(asOrderEntry));
    };

    // §Item 4 — in-situ display-name edit.
    const [editingCode, setEditingCode] = useState(null);
    const [editValue, setEditValue] = useState("");
    const beginEdit = (row) => {
        setEditingCode(row.head.template_code);
        setEditValue(row.head.display_name
            || row.current?.display_name || "");
    };
    const commitEdit = async (row) => {
        const code = row.head.template_code;
        const trimmed = (editValue || "").trim();
        const prev = row.head.display_name || row.current?.display_name || "";
        if (!trimmed) {
            toast.error("Display name cannot be empty");
            setEditingCode(null);
            return;
        }
        if (trimmed === prev) { setEditingCode(null); return; }
        // Optimistic update on the head projection.
        setItems(list => (list || []).map(x =>
            x.head.template_code === code
                ? { ...x, head: { ...x.head, display_name: trimmed } }
                : x));
        setEditingCode(null);
        try {
            await api.patch(`/admin/content-library/templates/${code}`,
                { display_name: trimmed });
            toast.success("Display name updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Rename failed");
            load();
        }
    };
    const cancelEdit = () => setEditingCode(null);

    // §Section Markers — add / inline-edit / delete state + helpers.
    const [showAddMarker, setShowAddMarker] = useState(false);
    const [newMarkerLabel, setNewMarkerLabel] = useState("");
    const [editingMarkerId, setEditingMarkerId] = useState(null);
    const [markerEditValue, setMarkerEditValue] = useState("");
    const [markerToDelete, setMarkerToDelete] = useState(null);

    const addMarker = async () => {
        const label = (newMarkerLabel || "").trim();
        if (!label) { toast.error("Label cannot be empty"); return; }
        try {
            const r = await api.post(
                "/admin/content-library/section-markers", { label });
            setMarkers(prev => [...prev, r.data]);
            setNewMarkerLabel("");
            setShowAddMarker(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message
                || "Failed to add marker");
        }
    };
    const beginMarkerEdit = (m) => {
        setEditingMarkerId(m.id);
        setMarkerEditValue(m.label || "");
    };
    const commitMarkerEdit = async (m) => {
        const label = (markerEditValue || "").trim();
        if (!label) {
            toast.error("Label cannot be empty");
            setEditingMarkerId(null);
            return;
        }
        if (label === m.label) { setEditingMarkerId(null); return; }
        setMarkers(list => (list || []).map(
            x => x.id === m.id ? { ...x, label } : x));
        setEditingMarkerId(null);
        try {
            await api.patch(
                `/admin/content-library/section-markers/${m.id}`, { label });
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Rename failed");
            load();
        }
    };
    const cancelMarkerEdit = () => setEditingMarkerId(null);
    const confirmDeleteMarker = async () => {
        const m = markerToDelete;
        if (!m) return;
        try {
            await api.delete(
                `/admin/content-library/section-markers/${m.id}`);
            setMarkers(prev => prev.filter(x => x.id !== m.id));
            setMarkerToDelete(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message
                || "Delete failed");
        }
    };

    const chipCls = (active) => `text-[12px] px-2.5 py-1 rounded-full border transition ${
        active
            ? "bg-charcoal text-white border-charcoal"
            : "bg-white text-charcoal border-[#E5E2DC] hover:bg-[#FAFAFA]"
    }`;
    const groupLabel = "text-[11px] uppercase font-manrope font-semibold text-[#666] mb-1";

    return (
        <div className="flex flex-col gap-6" data-testid="cl-list">
            <h1 className="text-2xl font-manrope font-bold text-charcoal">Communication Content Library</h1>
            <p className="text-sm text-slate-600 -mt-4">Templates + immutable published versions. Historical versions cannot be edited — publish a new version to change wording.</p>

            <div className="flex flex-col gap-3" data-testid="cl-filters">
                {/* §CL Layout Pass — single 2-column grid; separator
                    dropped, horizontal gap tightened to gap-x-6 and
                    vertical spacing to gap-y-3 so the two rows sit as
                    a compact block rather than a divided cluster. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                    <div>
                        <div className={groupLabel}>Workstages</div>
                        <div className="flex flex-wrap gap-2">
                            {[1, 2, 3, 4, 5, 6, 7].map(n => (
                                <button key={n}
                                    onClick={() => toggle(setWsSel)(String(n))}
                                    aria-pressed={wsSel.has(String(n))}
                                    data-testid={`cl-filter-ws-${n}`}
                                    className={chipCls(wsSel.has(String(n)))}>WS{n}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className={groupLabel}>Class</div>
                        <div className="flex flex-wrap gap-2">
                            {CLASSES.map(c => (
                                <button key={c}
                                    onClick={() => toggle(setClassSel)(c)}
                                    aria-pressed={classSel.has(c)}
                                    data-testid={`cl-filter-class-${c}`}
                                    className={chipCls(classSel.has(c))}>{c}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className={groupLabel}>Delivery mode</div>
                        <div className="flex flex-wrap gap-2">
                            {MODES.map(m => (
                                <button key={m}
                                    onClick={() => toggle(setModeSel)(m)}
                                    aria-pressed={modeSel.has(m)}
                                    data-testid={`cl-filter-mode-${m}`}
                                    className={chipCls(modeSel.has(m))}>{m}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className={groupLabel}>Content status</div>
                        <div className="flex flex-wrap gap-2">
                            {STATUSES.map(s => (
                                <button key={s}
                                    onClick={() => toggle(setStatusSel)(s)}
                                    aria-pressed={statusSel.has(s)}
                                    data-testid={`cl-filter-status-${s}`}
                                    className={chipCls(statusSel.has(s))}>{s}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="pt-1">
                    {!showAddMarker ? (
                        <button data-testid="section-marker-add-button"
                            onClick={() => setShowAddMarker(true)}
                            className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded border border-[#E5E2DC] hover:bg-[#FAFAFA] text-charcoal">
                            <Plus size={14} /> Add section marker
                        </button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <input autoFocus
                                data-testid="section-marker-add-input"
                                value={newMarkerLabel}
                                onChange={(e) => setNewMarkerLabel(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") addMarker();
                                    else if (e.key === "Escape") {
                                        setShowAddMarker(false);
                                        setNewMarkerLabel("");
                                    }
                                }}
                                placeholder="Section label"
                                className="border border-[#E5E2DC] rounded px-2 py-1 text-[13px] w-[240px]" />
                            <button data-testid="section-marker-add-submit"
                                onClick={addMarker}
                                className="text-[12px] px-2.5 py-1 rounded bg-charcoal text-white">Add</button>
                            <button onClick={() => { setShowAddMarker(false); setNewMarkerLabel(""); }}
                                className="text-[12px] text-slate-500 hover:text-charcoal">Cancel</button>
                        </div>
                    )}
                </div>
            </div>

            {err ? <p className="text-sm text-rose-600 mt-4" data-testid="cl-err">Unable to load: {err}</p> : null}
            {items === null && !err ? <p className="text-sm text-slate-500 mt-4" data-testid="cl-loading">Loading…</p> : null}
            {items && mergedFiltered.length === 0 ? <p className="text-sm text-slate-500 mt-4" data-testid="cl-empty">No templates match the current filters.</p> : null}

            {items && mergedFiltered.length > 0 && (
                <table className="w-full mt-4 text-sm" data-testid="cl-table">
                    <thead>
                        <tr className="text-left border-b border-slate-200 text-slate-500">
                            <th className="py-2 w-8"></th>
                            <th className="py-2">Display name</th>
                            <th>Template code</th>
                            <th>Class</th>
                            <th>Delivery</th>
                            <th>WS</th>
                            <th>Status</th>
                            <th>Version</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {mergedFiltered.map((row) => {
                            const rowKey = row.key;
                            const isDragging = dragIndex === rowKey;
                            const isDropTarget = dropTarget === rowKey;
                            const dropCls = `${isDragging ? "opacity-50" : ""} ${
                                isDropTarget && !isDragging ? "border-t-2 border-terracotta" : ""
                            }`;

                            if (row.__row === "marker") {
                                const m = row;
                                const editing = editingMarkerId === m.id;
                                return (
                                    <tr key={`m:${m.id}`}
                                        draggable
                                        onDragOver={(e) => { e.preventDefault(); setDropTarget(rowKey); }}
                                        onDragLeave={() => { if (dropTarget === rowKey) setDropTarget(null); }}
                                        onDrop={() => handleDrop(rowKey)}
                                        onDragEnd={() => { setDragIndex(null); setDropTarget(null); }}
                                        className={`group bg-[#F5F3EE] border-y border-[#E5E2DC] ${dropCls}`}
                                        data-testid={`section-marker-row-${m.id}`}>
                                        <td colSpan={9} className="px-2 py-1.5">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    draggable
                                                    onDragStart={(e) => {
                                                        setDragIndex(rowKey);
                                                        e.dataTransfer.effectAllowed = "move";
                                                        try { e.dataTransfer.setData("text/plain", `marker:${m.id}`); } catch { /* noop */ }
                                                    }}
                                                    data-testid={`section-marker-drag-${m.id}`}
                                                    aria-label="Drag to reorder"
                                                    className={`inline-flex text-slate-400 hover:text-charcoal ${
                                                        isDragging ? "cursor-grabbing" : "cursor-grab"
                                                    }`}>
                                                    <GripVertical size={14} />
                                                </span>
                                                {editing ? (
                                                    <input autoFocus
                                                        value={markerEditValue}
                                                        onChange={(e) => setMarkerEditValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") commitMarkerEdit(m);
                                                            else if (e.key === "Escape") cancelMarkerEdit();
                                                        }}
                                                        onBlur={() => commitMarkerEdit(m)}
                                                        onFocus={(e) => e.target.select()}
                                                        data-testid={`section-marker-edit-input-${m.id}`}
                                                        className="border border-[#E5E2DC] rounded px-2 py-0.5 text-[11px] uppercase tracking-wide font-manrope font-semibold text-charcoal w-full max-w-[320px] bg-white" />
                                                ) : (
                                                    <span
                                                        onClick={() => beginMarkerEdit(m)}
                                                        data-testid={`section-marker-label-${m.id}`}
                                                        className="text-[11px] uppercase tracking-wide font-manrope font-semibold text-[#666] cursor-text flex-1">
                                                        {m.label}
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => setMarkerToDelete(m)}
                                                    data-testid={`section-marker-delete-${m.id}`}
                                                    aria-label={`Delete ${m.label}`}
                                                    title="Delete section marker"
                                                    className="opacity-0 group-hover:opacity-100 transition text-slate-500 hover:text-terracotta">
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }

                            const { head, current } = row;
                            const shownName = head.display_name
                                || current?.display_name || "—";
                            const editing = editingCode === head.template_code;
                            return (
                                <tr key={head.template_code}
                                    draggable
                                    onDragOver={(e) => { e.preventDefault(); setDropTarget(rowKey); }}
                                    onDragLeave={() => { if (dropTarget === rowKey) setDropTarget(null); }}
                                    onDrop={() => handleDrop(rowKey)}
                                    onDragEnd={() => { setDragIndex(null); setDropTarget(null); }}
                                    className={`border-b border-slate-100 ${dropCls}`}
                                    data-testid={`cl-row-${head.template_code}`}>
                                    <td className="py-2 align-middle">
                                        <span
                                            draggable
                                            onDragStart={(e) => {
                                                setDragIndex(rowKey);
                                                e.dataTransfer.effectAllowed = "move";
                                                try { e.dataTransfer.setData("text/plain", head.template_code); } catch { /* noop */ }
                                            }}
                                            data-testid={`cl-drag-${head.template_code}`}
                                            aria-label="Drag to reorder"
                                            className={`inline-flex text-slate-400 hover:text-charcoal ${
                                                isDragging ? "cursor-grabbing" : "cursor-grab"
                                            }`}>
                                            <GripVertical size={16} />
                                        </span>
                                    </td>
                                    <td className="py-2 pr-3 align-middle group"
                                        data-testid={`template-display-name-${head.template_code}`}>
                                        {editing ? (
                                            <input
                                                autoFocus
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") commitEdit(row);
                                                    else if (e.key === "Escape") cancelEdit();
                                                }}
                                                onBlur={() => commitEdit(row)}
                                                onFocus={(e) => e.target.select()}
                                                data-testid={`template-display-name-input-${head.template_code}`}
                                                className="border border-[#E5E2DC] rounded px-2 py-1 text-[13px] w-full max-w-[320px]"
                                            />
                                        ) : (
                                            <span
                                                onClick={() => beginEdit(row)}
                                                className="inline-flex items-center gap-1.5 cursor-text">
                                                <span className="font-manrope font-semibold text-charcoal">{shownName}</span>
                                                <Pencil size={12}
                                                    className="text-slate-300 group-hover:text-slate-500" />
                                            </span>
                                        )}
                                    </td>
                                    <td className="font-mono text-xs">{head.template_code}</td>
                                    <td>{current?.communication_class || "—"}</td>
                                    <td>{current?.delivery_control_mode || "—"}</td>
                                    <td>{(current?.applicable_workstages || []).join(",")}</td>
                                    <td><StatusPill value={current?.content_status} /></td>
                                    <td className="font-mono text-xs">v{current?.version || "-"}</td>
                                    <td>
                                        <Link to={`/admin/content-library/${head.template_code}`}
                                                data-testid={`cl-view-${head.template_code}`}
                                                className="text-terracotta hover:underline font-semibold">View</Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {markerToDelete && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
                    data-testid={`section-marker-delete-confirm-${markerToDelete.id}`}>
                    <div className="bg-white rounded-lg p-4 w-[380px]">
                        <h3 className="text-sm font-manrope font-bold text-charcoal">Delete section marker</h3>
                        <p className="text-[13px] text-slate-600 mt-2">
                            Delete section marker <span className="font-semibold">‘{markerToDelete.label}’</span>? Templates in this section will remain.
                        </p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => setMarkerToDelete(null)}
                                data-testid={`section-marker-delete-cancel-${markerToDelete.id}`}
                                className="px-3 py-1.5 rounded border border-[#E5E2DC] text-[12px]">Cancel</button>
                            <button onClick={confirmDeleteMarker}
                                data-testid={`section-marker-delete-confirm-btn-${markerToDelete.id}`}
                                className="px-3 py-1.5 rounded bg-rose-600 text-white text-[12px] font-semibold">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ContentLibraryDetail() {
    const { code } = useParams();
    const navigate = useNavigate();
    const [head, setHead] = useState(null);
    const [versions, setVersions] = useState(null);
    const [showPublish, setShowPublish] = useState(false);
    const [preview, setPreview] = useState(null);
    const [identities, setIdentities] = useState(null);
    const [err, setErr] = useState("");
    const templateLabel = head?.name || head?.code || code || "Template";
    usePageMeta({ title: `${templateLabel} · Content Library | Studio G4 Admin` });

    useEffect(() => {
        api.get(`/admin/content-library/templates/${code}`).then(r => setHead(r.data)).catch(e => setErr(e.message));
        api.get(`/admin/content-library/templates/${code}/versions`).then(r => setVersions(r.data.items || [])).catch(e => setErr(e.message));
        // WP3b/WP3a — resolve identity keys for operator display next to the
        // raw "g4"/"lrs" value stored on the template version.
        api.get(`/admin/email-identities`).then(r => setIdentities(r.data || {})).catch(() => setIdentities({}));
    }, [code]);

    async function handlePublish(form) {
        try {
            const r = await api.post(`/admin/content-library/templates/${code}/versions`, form);
            toast.success(`Published v${r.data.version}`);
            setShowPublish(false);
            const v = await api.get(`/admin/content-library/templates/${code}/versions`);
            setVersions(v.data.items || []);
            const h = await api.get(`/admin/content-library/templates/${code}`);
            setHead(h.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Publish rejected");
        }
    }

    if (err) return <div className="py-6 px-4"><p className="text-rose-600" data-testid="cl-detail-err">Error: {err}</p></div>;
    if (!head) return <div className="py-6 px-4" data-testid="cl-detail-loading">Loading…</div>;

    const current = head.current;
    return (
        <div className="py-6 px-4" data-testid="cl-detail">
            <button onClick={() => navigate("/admin/content-library")}
                    className="text-sm text-slate-500 hover:text-charcoal" data-testid="cl-back">← Content Library</button>
            <h1 className="text-2xl font-manrope font-bold text-charcoal mt-2">{code}</h1>
            {current ? (
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <StatusPill value={current.content_status} />
                    <span className="px-2 py-0.5 rounded bg-slate-100">{current.communication_class}</span>
                    <span className="px-2 py-0.5 rounded bg-slate-100">{current.delivery_control_mode}</span>
                    {current.editable_by_admin && <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid="cl-editable-badge">editable</span>}
                    {current.legal_review_required && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="cl-legal-badge">legal-review</span>}
                    <span className="px-2 py-0.5 rounded bg-slate-100">v{current.version} (current)</span>
                </div>
            ) : null}

            <div className="mt-6">
                <div className="flex items-center justify-between">
                    <h2 className="font-manrope font-semibold">Versions (read-only historical)</h2>
                    <button data-testid="cl-publish-open"
                            onClick={() => setShowPublish(true)}
                            className="px-3 py-1.5 rounded bg-terracotta text-white text-sm font-semibold">Publish new version</button>
                </div>
                {versions && (
                    <table className="w-full mt-3 text-sm" data-testid="cl-versions-table">
                        <thead><tr className="text-left border-b border-slate-200 text-slate-500">
                            <th className="py-2">v</th><th>Subject</th><th>Status</th><th>Editable</th><th>Created</th>
                        </tr></thead>
                        <tbody>
                            {versions.map(v => (
                                <tr key={v.id} className="border-b border-slate-100" data-testid={`cl-version-row-${v.version}`}>
                                    <td className="py-2 font-mono text-xs">v{v.version}</td>
                                    <td className="max-w-md truncate">{v.default_subject}</td>
                                    <td><StatusPill value={v.content_status} /></td>
                                    <td className="text-xs">{v.editable_by_admin ? "yes" : "no"}</td>
                                    <td className="text-xs text-slate-500">{v.created_at ? String(v.created_at).slice(0, 19) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {current && (
                <div className="mt-8">
                    <h2 className="font-manrope font-semibold">Current version preview</h2>
                    <div className="mt-2 border rounded p-3 bg-white text-sm">
                        {/* WP3c — sender identity (read-only).  Admins reviewing
                            per plan §13 task U3.3 can confirm From / Reply-To /
                            signature variant here without opening edit mode.
                            Post-WP3b: `default_from_identity` / `default_reply_to`
                            are short keys ("g4" / "lrs"); the resolved env
                            value is shown alongside via /admin/email-identities. */}
                        <div className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-1.5 pb-3 mb-3 border-b border-slate-100">
                            <p className="text-xs text-slate-500">Sender identity</p>
                            {current.default_from_identity ? (
                                <p className="font-mono text-xs text-charcoal"
                                   data-testid="cl-detail-from-identity">
                                    {current.default_from_identity}
                                    {identities && (
                                        <span className="ml-2 text-slate-500">
                                            → {identities[current.default_from_identity]?.from
                                                ?? <span className="italic text-amber-600">(not configured)</span>}
                                        </span>
                                    )}
                                </p>
                            ) : (
                                <p className="font-mono text-xs text-slate-400 italic"
                                   data-testid="cl-detail-from-identity">— (inherit — resolver falls back to env/variant default)</p>
                            )}
                            <p className="text-xs text-slate-500">Reply-To</p>
                            {current.default_reply_to ? (
                                <p className="font-mono text-xs text-charcoal"
                                   data-testid="cl-detail-reply-to">
                                    {current.default_reply_to}
                                    {identities && (
                                        <span className="ml-2 text-slate-500">
                                            → {identities[current.default_reply_to]?.reply_to
                                                ?? <span className="italic text-amber-600">(not configured)</span>}
                                        </span>
                                    )}
                                </p>
                            ) : (
                                <p className="font-mono text-xs text-slate-400 italic"
                                   data-testid="cl-detail-reply-to">— (inherit — resolver falls back to env/variant default)</p>
                            )}
                            <p className="text-xs text-slate-500">Signature variant</p>
                            {current.signature_block_variant ? (
                                <p className="font-mono text-xs text-charcoal"
                                   data-testid="cl-detail-signature-variant">{current.signature_block_variant}</p>
                            ) : (
                                <p className="font-mono text-xs text-slate-400 italic"
                                   data-testid="cl-detail-signature-variant">— (inherit — resolver falls back to env/variant default)</p>
                            )}
                        </div>
                        <p className="text-xs text-slate-500">Subject template</p>
                        <p className="mt-1 font-mono">{current.default_subject}</p>
                        <p className="text-xs text-slate-500 mt-3">Body template</p>
                        <pre className="mt-1 font-mono text-xs whitespace-pre-wrap">{current.default_body}</pre>
                    </div>
                    <button data-testid="cl-preview-btn" className="mt-3 text-sm text-terracotta font-semibold"
                            onClick={async () => {
                                try {
                                    const r = await api.post(`/admin/content-library/preview`, { version_id: current.id, merge_fields: {} });
                                    setPreview(r.data);
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail?.message || "Preview failed");
                                }
                            }}>Render preview →</button>
                    {preview && (
                        <div className="mt-3 border rounded p-3 bg-slate-50 text-sm" data-testid="cl-preview-output">
                            <p className="text-xs text-slate-500">Rendered subject</p>
                            <p className="mt-1">{preview.subject}</p>
                            <p className="text-xs text-slate-500 mt-3">Rendered body</p>
                            <pre className="mt-1 text-xs whitespace-pre-wrap">{preview.body}</pre>
                        </div>
                    )}
                </div>
            )}

            {showPublish && (
                <PublishNewVersion current={current} onCancel={() => setShowPublish(false)} onPublish={handlePublish} />
            )}
        </div>
    );
}

function PublishNewVersion({ current, onCancel, onPublish }) {
    const [f, setF] = useState({
        display_name: current?.display_name || "",
        category: current?.category || "",
        trigger_code: current?.trigger_code || "",
        communication_class: current?.communication_class || "operational",
        delivery_control_mode: current?.delivery_control_mode || "admin_confirmed",
        default_subject: current?.default_subject || "",
        default_body: current?.default_body || "",
        applicable_workstages: (current?.applicable_workstages || []).join(","),
        applicable_project_states: (current?.applicable_project_states || []).join(","),
        content_status: "draft",
        editable_by_admin: current?.editable_by_admin || false,
        legal_review_required: current?.legal_review_required || false,
        default_from_identity: current?.default_from_identity || "",
        default_reply_to: current?.default_reply_to || "",
        signature_block_variant: current?.signature_block_variant || "",
    });
    const opId = useMemo(() => `pub-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`, []);

    // §Merge-Fields Pass — Item 1: auto-scan body + subject for
    //   {{token}} placeholders.  Each unique token becomes a row in
    //   the supported list with `required: true` defaulted (matching
    //   the v1 seed convention).  Users may toggle Required or prune
    //   a token via the X button; pruned tokens are remembered so a
    //   subsequent auto-scan does NOT re-add them.
    const TOKEN_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
    const [supportedMergeFields, setSupportedMergeFields] = useState(() => {
        const seed = (current?.supported_merge_fields || []).map(m => ({
            key: m.key,
            required: !!m.required,
        }));
        return seed;
    });
    const [pruned, setPruned] = useState(() => new Set());
    const scannedTokens = useMemo(() => {
        const s = new Set();
        for (const src of [f.default_subject || "", f.default_body || ""]) {
            let m;
            const re = new RegExp(TOKEN_RE.source, "g");
            while ((m = re.exec(src)) !== null) s.add(m[1]);
        }
        return s;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [f.default_subject, f.default_body]);
    useEffect(() => {
        setSupportedMergeFields(prev => {
            const byKey = new Map(prev.map(x => [x.key, x]));
            for (const tok of scannedTokens) {
                if (!byKey.has(tok) && !pruned.has(tok)) {
                    byKey.set(tok, { key: tok, required: true });
                }
            }
            // Remove entries whose token no longer appears in the text
            // AND was auto-suggested (not manually pruned).  Manually
            // declared tokens the user added earlier stay put.  We
            // approximate manual-declared by preserving anything that
            // was in `current.supported_merge_fields` — those come
            // from a prior publish.
            const preserved = new Set(
                (current?.supported_merge_fields || []).map(m => m.key));
            const next = [];
            for (const [k, v] of byKey) {
                if (scannedTokens.has(k) || preserved.has(k)) next.push(v);
            }
            return next;
        });
    }, [scannedTokens, pruned, current]);

    const toggleRequired = (key) => setSupportedMergeFields(prev =>
        prev.map(m => m.key === key ? { ...m, required: !m.required } : m));
    const removeField = (key) => {
        setPruned(prev => { const n = new Set(prev); n.add(key); return n; });
        setSupportedMergeFields(prev => prev.filter(m => m.key !== key));
    };

    const declaredKeys = new Set(supportedMergeFields.map(m => m.key));
    const undeclaredTokens = Array.from(scannedTokens)
        .filter(t => !declaredKeys.has(t));

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="cl-publish-modal">
            <div className="bg-white rounded-lg p-4 w-[600px] max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-manrope font-bold">Publish new version</h3>
                <p className="text-xs text-slate-500 mt-1">Publishing creates a NEW version. Historical versions are never mutated.</p>
                <div className="mt-3 space-y-2 text-sm">
                    <label className="block">Display name<input data-testid="cl-publish-display-name" className="mt-1 w-full border rounded px-2 py-1" value={f.display_name} onChange={e => setF({ ...f, display_name: e.target.value })} /></label>
                    <label className="block">Trigger code<input data-testid="cl-publish-trigger" className="mt-1 w-full border rounded px-2 py-1 font-mono text-xs" value={f.trigger_code} onChange={e => setF({ ...f, trigger_code: e.target.value })} /></label>
                    <label className="block">Subject<input data-testid="cl-publish-subject" className="mt-1 w-full border rounded px-2 py-1" value={f.default_subject} onChange={e => setF({ ...f, default_subject: e.target.value })} /></label>
                    <label className="block">Body<textarea data-testid="cl-publish-body" rows={6} className="mt-1 w-full border rounded px-2 py-1 font-mono text-xs" value={f.default_body} onChange={e => setF({ ...f, default_body: e.target.value })} /></label>

                    {/* §Merge-Fields Pass — Item 1: merge-fields section. */}
                    <div className="border-t pt-3">
                        <div className="flex items-center justify-between">
                            <div className="font-manrope font-semibold text-[13px]">Merge fields</div>
                            <div className="text-[11px] text-slate-500">Auto-scanned from subject + body</div>
                        </div>
                        {undeclaredTokens.length > 0 && (
                            <div className="mt-2 border border-terracotta bg-[#FDF3EF] text-terracotta text-[12px] rounded p-2"
                                 data-testid="template-merge-fields-warning">
                                The following tokens appear in your text but are marked as unsupported and will fail on render: {undeclaredTokens.map(t => <code key={t} className="font-mono">{`{{${t}}}`}</code>).reduce((prev, curr) => prev.length ? [...prev, ", ", curr] : [curr], [])}. Either re-add them to the supported list or remove them from the text.
                            </div>
                        )}
                        <div className="mt-2 space-y-1" data-testid="template-merge-fields-list">
                            {supportedMergeFields.length === 0 && (
                                <div className="text-[12px] text-slate-500">No merge fields declared. Add tokens like <code className="font-mono">{`{{first_name}}`}</code> to the subject or body — they will appear here automatically.</div>
                            )}
                            {supportedMergeFields.map(m => (
                                <div key={m.key}
                                    data-testid={`template-merge-field-row-${m.key}`}
                                    className="flex items-center justify-between gap-2 border border-slate-200 rounded px-2 py-1.5">
                                    <code className="font-mono text-[12px] text-slate-800">{m.key}</code>
                                    <div className="flex items-center gap-3">
                                        <label className="text-[12px] flex items-center gap-1">
                                            <input type="checkbox"
                                                checked={m.required}
                                                onChange={() => toggleRequired(m.key)}
                                                data-testid={`template-merge-field-required-${m.key}`} />
                                            Required
                                        </label>
                                        <button
                                            onClick={() => removeField(m.key)}
                                            data-testid={`template-merge-field-remove-${m.key}`}
                                            aria-label={`Remove ${m.key}`}
                                            title={`Remove ${m.key}`}
                                            className="text-[12px] text-slate-500 hover:text-terracotta px-1">
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">Class
                            <select data-testid="cl-publish-class" className="mt-1 w-full border rounded px-2 py-1" value={f.communication_class} onChange={e => setF({ ...f, communication_class: e.target.value })}>
                                {CLASSES.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="block">Delivery
                            <select data-testid="cl-publish-mode" className="mt-1 w-full border rounded px-2 py-1" value={f.delivery_control_mode} onChange={e => setF({ ...f, delivery_control_mode: e.target.value })}>
                                {MODES.map(m => <option key={m}>{m}</option>)}
                            </select>
                        </label>
                    </div>
                    <label className="block">Content status
                        <select data-testid="cl-publish-status" className="mt-1 w-full border rounded px-2 py-1" value={f.content_status} onChange={e => setF({ ...f, content_status: e.target.value })}>
                            {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                    </label>

                    {/* WP3b — per-template sender identity key.  Short key is
                        resolved to the matching env var at send time.  See
                        stage5_b3_5_send.resolve_email_identity. */}
                    <div className="border-t pt-3 space-y-2">
                        <div className="font-manrope font-semibold text-[13px]">Sender identity (WP3b)</div>
                        <label className="block">Sender identity
                            <select data-testid="cl-publish-from-identity"
                                    className="mt-1 w-full border rounded px-2 py-1 font-mono text-xs"
                                    value={f.default_from_identity}
                                    onChange={e => setF({ ...f, default_from_identity: e.target.value })}>
                                <option value="">(unset)</option>
                                <option value="g4">g4 — Studio G4</option>
                                <option value="lrs">lrs — London Renovation Specialists</option>
                            </select>
                        </label>
                        <label className="block">Reply-To
                            <select data-testid="cl-publish-reply-to"
                                    className="mt-1 w-full border rounded px-2 py-1 font-mono text-xs"
                                    value={f.default_reply_to}
                                    onChange={e => setF({ ...f, default_reply_to: e.target.value })}>
                                <option value="">(unset)</option>
                                <option value="g4">g4 — Studio G4</option>
                                <option value="lrs">lrs — London Renovation Specialists</option>
                            </select>
                        </label>
                        <label className="block">Signature variant
                            <select data-testid="cl-publish-signature-variant"
                                    className="mt-1 w-full border rounded px-2 py-1"
                                    value={f.signature_block_variant}
                                    onChange={e => setF({ ...f, signature_block_variant: e.target.value })}>
                                <option value="">(none set)</option>
                                <option value="LRS-signature">LRS-signature</option>
                                <option value="G4-signature">G4-signature</option>
                            </select>
                        </label>
                    </div>
                    <label className="flex items-center gap-2"><input type="checkbox" data-testid="cl-publish-editable" checked={f.editable_by_admin} onChange={e => setF({ ...f, editable_by_admin: e.target.checked })} /> editable_by_admin (allow per-send subject/body override)</label>
                    <label className="flex items-center gap-2"><input type="checkbox" data-testid="cl-publish-legal" checked={f.legal_review_required} onChange={e => setF({ ...f, legal_review_required: e.target.checked })} /> legal-review required</label>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <button data-testid="cl-publish-cancel" className="px-3 py-1.5 rounded border" onClick={onCancel}>Cancel</button>
                    <button data-testid="cl-publish-submit" className="px-3 py-1.5 rounded bg-terracotta text-white font-semibold"
                            onClick={() => onPublish({
                                ...f,
                                applicable_workstages: f.applicable_workstages.split(",").map(x => x.trim()).filter(Boolean).map(Number),
                                applicable_project_states: f.applicable_project_states.split(",").map(x => x.trim()).filter(Boolean),
                                supported_merge_fields: supportedMergeFields,
                                default_from_identity: f.default_from_identity || null,
                                default_reply_to: f.default_reply_to || null,
                                signature_block_variant: f.signature_block_variant || null,
                                publication_op_id: opId,
                            })}>Publish</button>
                </div>
            </div>
        </div>
    );
}
